import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, Plus } from "lucide-react";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { Button } from "@/components/ui/Button";
import type {
  ContratoDto,
  MaquinarioDto,
  OperationalMetricsDto,
  PeriodRange,
  RegistroHorimetroDto,
} from "@/services/locacoes";
import { isEquipamentoAtivo } from "@/services/locacoes";
import { formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import { defaultConsumoLimiteLphForTipo, getMaquinaTipoImagemUrl } from "./maquinaTipoImagens";
import {
  computeHorasPorDiaFiltrado,
  computeRegistrosPorDiaFiltrado,
  formatHoras,
  type buildMaquinasOperacao,
} from "./locacoesUtils";

type ConsumoRow = OperationalMetricsDto["consumoPorMaquina"][number];
type MaquinaOperacaoRow = ReturnType<typeof buildMaquinasOperacao>[number];

const filterSelectClass =
  "h-7 max-w-[9rem] min-w-[6rem] cursor-pointer rounded border border-slate-200 bg-white px-2 text-[11px] text-slate-800 outline-none focus:ring-1 focus:ring-brand-500";

interface Props {
  rows: ConsumoRow[];
  maquinarios: MaquinarioDto[];
  contratos: ContratoDto[];
  maquinasOperacao: MaquinaOperacaoRow[];
  registrosHorimetro: RegistroHorimetroDto[];
  period: PeriodRange;
  scopeContratoId?: string;
  scopeMaquinaId?: string;
  onNovaMaquina?: () => void;
  /** Oculta produtividade e eficiência L/h (ex.: filtro de máquina na visão geral). */
  hideComparativoCharts?: boolean;
}

function truncateNome(nome: string, max = 12): string {
  return nome.length > max ? `${nome.slice(0, max)}…` : nome;
}

function sortContratosPorCodigo(a: ContratoDto, b: ContratoDto): number {
  const anoDiff = (b.codigoAno ?? 0) - (a.codigoAno ?? 0);
  if (anoDiff !== 0) return anoDiff;
  return (b.codigoSequencial ?? 0) - (a.codigoSequencial ?? 0);
}

export function EficienciaMaquinasPanel({
  rows,
  maquinarios,
  contratos,
  maquinasOperacao,
  registrosHorimetro,
  period,
  scopeContratoId,
  scopeMaquinaId,
  onNovaMaquina,
  hideComparativoCharts = false,
}: Props) {
  const [filtroContratoId, setFiltroContratoId] = useState(scopeContratoId ?? "");
  const [filtroMaquinaId, setFiltroMaquinaId] = useState(scopeMaquinaId ?? "");

  useEffect(() => {
    setFiltroContratoId(scopeContratoId ?? "");
    setFiltroMaquinaId(scopeMaquinaId ?? "");
  }, [scopeContratoId, scopeMaquinaId, period.startDate, period.endDate]);

  const contratoSelecionado = useMemo(
    () => (filtroContratoId ? contratos.find((c) => c.id === filtroContratoId) ?? null : null),
    [contratos, filtroContratoId],
  );

  const maquinasFiltro = useMemo(() => {
    if (!contratoSelecionado) return maquinarios;
    const ids = new Set(contratoSelecionado.maquinaIds);
    return maquinarios.filter((m) => ids.has(m.id));
  }, [maquinarios, contratoSelecionado]);

  const chartFilterOpts = useMemo(
    () => ({
      maquinaId: filtroMaquinaId || undefined,
      contrato: contratoSelecionado,
    }),
    [filtroMaquinaId, contratoSelecionado],
  );

  const horasPorDiaFiltrado = useMemo(
    () => computeHorasPorDiaFiltrado(registrosHorimetro, period, chartFilterOpts),
    [registrosHorimetro, period, chartFilterOpts],
  );

  const registrosPorDiaFiltrado = useMemo(
    () => computeRegistrosPorDiaFiltrado(registrosHorimetro, period, chartFilterOpts),
    [registrosHorimetro, period, chartFilterOpts],
  );

  const maquinaSelecionada = filtroMaquinaId
    ? maquinarios.find((m) => m.id === filtroMaquinaId) ?? null
    : null;

  const consumoPorMaquinaMap = useMemo(() => {
    const map = new Map<string, ConsumoRow>();
    for (const r of rows) map.set(r.maquinaId, r);
    return map;
  }, [rows]);

  const desempenhoItems = useMemo(() => {
    return maquinarios
      .filter((m) => !isEquipamentoAtivo(m))
      .map((m) => {
        const row = consumoPorMaquinaMap.get(m.id);
        const consumoLimiteLph = m.consumoLimiteLph ?? defaultConsumoLimiteLphForTipo(m.tipo);
        const litrosPorHora = row?.litrosPorHora ?? 0;
        const horas = row?.horas ?? 0;
        const alerta = consumoLimiteLph > 0 && litrosPorHora > consumoLimiteLph;
        return {
          maquinaId: m.id,
          maquinaNome: m.nome,
          tipo: m.tipo,
          litrosPorHora,
          consumoLimiteLph,
          horas,
          litros: row?.litros ?? 0,
          alerta,
        };
      })
      .sort((a, b) => {
        if (b.litrosPorHora !== a.litrosPorHora) return b.litrosPorHora - a.litrosPorHora;
        return a.maquinaNome.localeCompare(b.maquinaNome, "pt-BR");
      });
  }, [maquinarios, consumoPorMaquinaMap]);

  const eficienciaData = desempenhoItems
    .filter((d) => d.litrosPorHora > 0 || d.horas > 0)
    .map((d) => ({
      ...d,
      nomeCurto: truncateNome(d.maquinaNome),
    }));

  const produtividadeData = [...maquinasOperacao]
    .filter((m) => m.horasPeriodo > 0 || m.registrosPeriodo > 0)
    .sort((a, b) => b.horasPeriodo - a.horasPeriodo)
    .slice(0, 8)
    .map((m) => ({
      maquinaId: m.id,
      nomeCurto: truncateNome(m.nome),
      maquinaNome: m.nome,
      horas: m.horasPeriodo,
    }));

  const chartDataHoras = horasPorDiaFiltrado.map((d) => ({
    dia: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`,
    valor: d.horas,
  }));
  const chartDataRegistros = registrosPorDiaFiltrado.map((d) => ({
    dia: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`,
    valor: d.count,
  }));
  const usarHorasNoGrafico = chartDataHoras.some((d) => d.valor > 0);
  const chartDataDiario = usarHorasNoGrafico ? chartDataHoras : chartDataRegistros;

  const horasChartTitle = usarHorasNoGrafico
    ? maquinaSelecionada
      ? `Horas trabalhadas — ${maquinaSelecionada.nome}`
      : "Horas trabalhadas"
    : maquinaSelecionada
      ? `Registros — ${maquinaSelecionada.nome}`
      : "Registros de horímetro";

  const horasChartSubtitle = [
    contratoSelecionado ? `Contrato ${contratoSelecionado.codigoContrato}` : null,
    !maquinaSelecionada ? "Todas as máquinas" : null,
    usarHorasNoGrafico ? "por dia no período" : "quantidade por dia",
  ]
    .filter(Boolean)
    .join(" · ");

  const horasChartFilters = (
    <div className="flex flex-wrap items-center gap-1.5">
      <select
        value={filtroContratoId}
        onChange={(e) => {
          const nextContrato = e.target.value;
          setFiltroContratoId(nextContrato);
          if (filtroMaquinaId && nextContrato) {
            const c = contratos.find((x) => x.id === nextContrato);
            if (c && !c.maquinaIds.includes(filtroMaquinaId)) {
              setFiltroMaquinaId("");
            }
          }
        }}
        className={filterSelectClass}
        aria-label="Filtrar horas por contrato"
      >
        <option value="">Todos contratos</option>
        {[...contratos].sort(sortContratosPorCodigo).map((c) => (
          <option key={c.id} value={c.id}>
            {c.codigoContrato}
          </option>
        ))}
      </select>
      <select
        value={filtroMaquinaId}
        onChange={(e) => setFiltroMaquinaId(e.target.value)}
        className={filterSelectClass}
        aria-label="Filtrar horas por máquina"
      >
        <option value="">Todas máquinas</option>
        {maquinasFiltro.map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome}
          </option>
        ))}
      </select>
    </div>
  );

  const maxEficienciaY = Math.max(
    ...eficienciaData.map((d) => Math.max(d.litrosPorHora, d.consumoLimiteLph)),
    1,
  );
  const eficienciaYMax = Math.ceil(maxEficienciaY * 1.15);

  const maxProdY = Math.max(...produtividadeData.map((d) => d.horas), 1);
  const prodYMax = Math.ceil(maxProdY * 1.12);

  const alertas = desempenhoItems.filter((d) => d.alerta);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {!hideComparativoCharts && (
          <>
            <ChartContainer
              title="Produtividade por máquina"
              subtitle="Horas trabalhadas no período"
              height={220}
              empty={produtividadeData.length === 0}
              emptyMessage="Sem horímetro no período."
              className="min-h-[280px]"
            >
              <BarChart data={produtividadeData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="nomeCurto" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis
                  domain={[0, prodYMax]}
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip
                  formatter={(v: unknown) => [formatHoras(Number(v)), "Horas"]}
                  labelFormatter={(_, payload) =>
                    (payload?.[0]?.payload as { maquinaNome?: string })?.maquinaNome ?? ""
                  }
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Bar dataKey="horas" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ChartContainer>

            <ChartContainer
              title="Eficiência (L/h) — consumo vs. horímetro"
              subtitle="Linha tracejada = limite de alerta por máquina"
              height={220}
              empty={eficienciaData.length === 0}
              emptyMessage="Sem abastecimentos e horímetro no período."
              className="min-h-[280px]"
            >
              <BarChart data={eficienciaData} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="nomeCurto" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis
                  domain={[0, eficienciaYMax]}
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null;
                    const d = payload[0].payload as (typeof eficienciaData)[number];
                    return (
                      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
                        <p className="font-semibold text-slate-800">{d.maquinaNome}</p>
                        <p className="text-amber-700">Limite: {formatNumber(d.consumoLimiteLph, 1)} L/h</p>
                        <p className={cn("font-medium", d.alerta ? "text-red-600" : "text-emerald-700")}>
                          Consumo: {formatNumber(d.litrosPorHora, 1)} L/h
                        </p>
                      </div>
                    );
                  }}
                />
                {eficienciaData.map((d) => (
                  <ReferenceLine
                    key={`lim-${d.maquinaId}`}
                    y={d.consumoLimiteLph}
                    stroke="#ef4444"
                    strokeDasharray="6 4"
                    strokeWidth={1.5}
                    ifOverflow="extendDomain"
                    label={
                      eficienciaData.length === 1
                        ? { value: "Limite", position: "insideTopRight", fill: "#ef4444", fontSize: 10 }
                        : undefined
                    }
                  />
                ))}
                <Bar dataKey="litrosPorHora" name="L/h" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  {eficienciaData.map((d) => (
                    <Cell key={d.maquinaId} fill={d.alerta ? "#ef4444" : "#22c55e"} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </>
        )}

        <div className="flex min-h-[280px] flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-card">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-slate-800">Desempenho por máquina</h3>
            {onNovaMaquina && (
              <Button type="button" variant="ghost" size="sm" onClick={onNovaMaquina} leftIcon={<Plus className="h-3.5 w-3.5" />}>
                Nova
              </Button>
            )}
          </div>
          <p className="mb-3 text-xs text-slate-500">Média de consumo (L/h) e limite por máquina no período</p>
          {desempenhoItems.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma máquina cadastrada.</p>
          ) : (
            <ul className="max-h-[220px] space-y-2 overflow-y-auto pr-1">
              {desempenhoItems.map((d) => {
                const maq = maquinarios.find((m) => m.id === d.maquinaId);
                const imgUrl = getMaquinaTipoImagemUrl(d.tipo, maq?.tipoAtivo);
                const pctLimite =
                  d.consumoLimiteLph > 0 && d.litrosPorHora > 0
                    ? Math.min(100, (d.litrosPorHora / d.consumoLimiteLph) * 100)
                    : 0;
                return (
                  <li
                    key={d.maquinaId}
                    className={cn(
                      "flex gap-2 rounded-lg border p-2",
                      d.alerta ? "border-red-200 bg-red-50/40" : "border-slate-100 bg-slate-50/50",
                    )}
                  >
                    <img
                      src={imgUrl}
                      alt={d.tipo || d.maquinaNome}
                      className="h-12 w-14 shrink-0 rounded-md border border-slate-200 object-cover bg-slate-100"
                      onError={(e) => {
                        e.currentTarget.src = "/locacoes/tipos/maquinario-default.jpg";
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-xs font-semibold text-slate-900">{d.maquinaNome}</p>
                        {d.alerta && <AlertTriangle className="h-3 w-3 shrink-0 text-red-600" />}
                      </div>
                      <p className="text-[10px] text-slate-500">
                        {d.litrosPorHora > 0
                          ? `${formatNumber(d.litrosPorHora, 1)} L/h · limite ${formatNumber(d.consumoLimiteLph, 1)}`
                          : `Sem consumo no período · limite ${formatNumber(d.consumoLimiteLph, 1)} L/h`}
                      </p>
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all",
                            d.litrosPorHora <= 0 ? "bg-slate-300" : d.alerta ? "bg-red-500" : "bg-emerald-500",
                          )}
                          style={{ width: `${pctLimite > 0 ? Math.max(4, pctLimite) : 0}%` }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <ChartContainer
          title={horasChartTitle}
          subtitle={horasChartSubtitle}
          height={220}
          empty={chartDataDiario.every((d) => d.valor === 0)}
          emptyMessage="Sem registros no período para os filtros selecionados."
          className="min-h-[280px]"
          headerAction={horasChartFilters}
        >
          <BarChart data={chartDataDiario} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="dia" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
            <Tooltip
              formatter={(v: unknown) => [
                usarHorasNoGrafico ? formatHoras(Number(v)) : `${Number(v)} registro(s)`,
                usarHorasNoGrafico ? "Horas" : "Registros",
              ]}
              labelFormatter={(label) => {
                const parts = [String(label)];
                if (maquinaSelecionada) parts.push(maquinaSelecionada.nome);
                if (contratoSelecionado) parts.push(contratoSelecionado.codigoContrato);
                return parts.join(" · ");
              }}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
            />
            <Bar
              dataKey="valor"
              fill={usarHorasNoGrafico ? "#22c55e" : "#0d9488"}
              radius={[4, 4, 0, 0]}
              maxBarSize={32}
            />
          </BarChart>
        </ChartContainer>
      </div>

      {alertas.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <strong>{alertas.length}</strong> máquina(s) com consumo acima do limite no período.
        </div>
      )}
    </div>
  );
}
